import { useEffect } from "react";
import shadeColor, { alphaColor } from "../utils/shadeColor";
import { layoutOf } from "../utils/selfcheckoutMode";

// Cor de fallback enquanto a loja não define a sua. Igual à de
// src/styles/tokens.css, que vale antes deste hook rodar.
const DEFAULT_BACKGROUND = "#dc3545";
const DEFAULT_TEXT = "#fff";

// Classe no <body> que liga o visual mobile (src/styles/mobile.css). Fica fora
// dos aparelhos de autoatendimento — totem, POS e tablet de mesa têm layout
// próprio —, mas vale no QR Code, em que o cliente pede pelo próprio celular.
export const MOBILE_UI_CLASS = "ui-mobile";

// Classe no <body> do totem e do tablet de mesa (src/styles/totem.css): fundo
// das páginas e ajustes de tela grande. O POS não recebe nenhuma das duas.
export const TOTEM_UI_CLASS = "ui-totem";

const VIEWPORT_OPEN = "width=device-width, initial-scale=1, viewport-fit=cover";
const VIEWPORT_LOCKED = `${VIEWPORT_OPEN}, maximum-scale=1, user-scalable=no`;

const useShopStyle = (shop) => {
  const loading = shop.name === "";
  const layout = layoutOf(shop);
  const hasMaxWidth = loading ? null : layout.maxWidth;
  const isMobileUi = loading ? null : layout.mobileUi;
  const isTotemUi = !loading && layout.totemUi;

  const backgroudColor = shop?.backgroudColor || DEFAULT_BACKGROUND;
  const textColor = shop?.textColor || DEFAULT_TEXT;

  useEffect(() => {
    if (!hasMaxWidth) return;
    const css = document.createElement("style");
    css.type = "text/css";
    css.async = false;
    css.innerHTML = `
      #root,
      .fixed-top,
      .fixed-bottom {
        max-width: 760px;
        margin: auto !important;
      }
    `;
    document.body.appendChild(css);

    // Sem esta limpeza cada montagem deixa mais uma tag <style> no body. Já
    // vazava antes do React 18 — a app republica a loja a cada 10s em
    // self-order — e o StrictMode só tornou o sintoma visível em dev.
    return () => {
      css.remove();
    };
  }, [hasMaxWidth]);

  // O zoom fica liberado no celular (index.html): bloqueá-lo é barreira de
  // acessibilidade. Nos aparelhos de autoatendimento o gesto de pinça só
  // atrapalha, então lá ele volta a ser travado.
  useEffect(() => {
    if (isMobileUi === null) return;
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        "content",
        isMobileUi ? VIEWPORT_OPEN : VIEWPORT_LOCKED,
      );
    }

    if (!isMobileUi) return;
    document.body.classList.add(MOBILE_UI_CLASS);
    return () => {
      document.body.classList.remove(MOBILE_UI_CLASS);
    };
  }, [isMobileUi]);

  useEffect(() => {
    if (!isTotemUi) return;
    document.body.classList.add(TOTEM_UI_CLASS);
    return () => {
      document.body.classList.remove(TOTEM_UI_CLASS);
    };
  }, [isTotemUi]);

  useEffect(() => {
    if (loading) return;
    const hover = shadeColor(backgroudColor, -0.1);
    const active = shadeColor(backgroudColor, -0.18);
    const css = document.createElement("style");
    css.type = "text/css";
    css.async = false;
    css.innerHTML = `
      :root {
        --shop-primary: ${backgroudColor};
        --shop-primary-hover: ${hover};
        --shop-primary-active: ${active};
        --shop-primary-soft: ${alphaColor(backgroudColor, 0.12)};
        --shop-primary-focus: ${alphaColor(backgroudColor, 0.35)};
        --shop-on-primary: ${textColor};
      }

      .bg-shop {
        background-color: ${backgroudColor};
      }

      .text-shop {
        color: ${textColor} !important;
      }

      /* Variante de botão no formato do Bootstrap 5: o .btn aplica as
         variáveis em cada estado (hover, active, focus-visible, disabled). */
      .btn-shop {
        --bs-btn-color: ${textColor};
        --bs-btn-bg: ${backgroudColor};
        --bs-btn-border-color: ${backgroudColor};
        --bs-btn-hover-color: ${textColor};
        --bs-btn-hover-bg: ${hover};
        --bs-btn-hover-border-color: ${hover};
        --bs-btn-active-color: ${textColor};
        --bs-btn-active-bg: ${active};
        --bs-btn-active-border-color: ${active};
        --bs-btn-disabled-color: ${textColor};
        --bs-btn-disabled-bg: ${backgroudColor};
        --bs-btn-disabled-border-color: ${backgroudColor};
        --bs-btn-disabled-opacity: 0.5;
        --bs-btn-focus-box-shadow: 0 0 0 0.2rem var(--shop-primary-focus);
      }

      /* Fora de .btn (opção escolhida no agendamento, "+" do upsell do
         totem) a classe só pinta na cor da loja. */
      .btn-shop:not(.btn) {
        border-color: ${backgroudColor};
        background-color: ${backgroudColor};
        color: ${textColor};
      }

      .btn-outline-shop {
        --bs-btn-color: ${backgroudColor};
        --bs-btn-border-color: ${backgroudColor};
        --bs-btn-hover-color: ${textColor};
        --bs-btn-hover-bg: ${backgroudColor};
        --bs-btn-hover-border-color: ${backgroudColor};
        --bs-btn-active-color: ${textColor};
        --bs-btn-active-bg: ${backgroudColor};
        --bs-btn-active-border-color: ${backgroudColor};
        --bs-btn-disabled-color: ${backgroudColor};
        --bs-btn-disabled-border-color: ${backgroudColor};
        --bs-btn-focus-box-shadow: 0 0 0 0.2rem var(--shop-primary-focus);
      }

      .btn-link {
        --bs-btn-color: ${backgroudColor};
        --bs-btn-hover-color: ${hover};
        --bs-btn-active-color: ${active};
      }

      .product-price-color-shop {
        color: ${backgroudColor};
      }

      .badge-shop {
        color: ${textColor};
        background-color: ${backgroudColor};
      }

      .border-color-shop {
        border-color: ${backgroudColor};
      }
    `;
    document.body.appendChild(css);

    // Idem: este efeito ainda re-roda toda vez que a loja muda de cor.
    return () => {
      css.remove();
    };
  }, [loading, backgroudColor, textColor]);
};

export default useShopStyle;
